import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, computed, DestroyRef, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ReactiveFormsModule } from '@angular/forms';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { IconoirExpand } from '@ey-xd/motif-icon';
import { ButtonSizes, MotifButtonModule, MotifFormsModule, MotifIconModule } from '@ey-xd/ng-motif';
import { PowerBIEmbeddedTokenDto } from '@models/webapi/dashboard.model';
import { CompanyPrimaryService } from '@models/webapi/user-profile';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { DASHBOARD_ACCESS } from '@routes';
import { EytcAuthService } from '@services/authentication/eytc-auth.service';
import { CompanySettingsService } from '@services/company-settings/company-settings.service';
import { DashboardService } from '@services/webapi/dashboard.service';
import { getFormattedEnumKeyByValue } from '@utils';
import * as pbi from 'powerbi-client';
import { PowerBIEmbedModule } from 'powerbi-client-angular';
import { PageComponent } from 'views/layout/main/page/page.component';

@Component({
  selector: 'app-dashboard',
  imports: [
    CommonModule,
    PowerBIEmbedModule,
    MotifButtonModule,
    MotifFormsModule,
    MotifIconModule,
    ReactiveFormsModule,
    TranslatePipe,
    PageComponent,
  ],
  templateUrl: './dashboard.component.html',
  standalone: true,
  styleUrls: ['./dashboard.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DashboardComponent {
  readonly #authService: EytcAuthService = inject(EytcAuthService);
  readonly #helperService = inject(DashboardService);
  readonly #sanitizer = inject(DomSanitizer);
  readonly #translate = inject(TranslateService);
  readonly #destroyRef = inject(DestroyRef);
  readonly IconoirExpand = IconoirExpand;
  readonly #companySettingsService = inject(CompanySettingsService);
  readonly primaryService = this.#companySettingsService.primaryService;

  public hide = false;
  public showErrorMessage = signal<boolean>(false);
  public sanitizedMessage = signal<SafeHtml | null>(null);
  public powerBIEmbeddedTokenDto = signal<PowerBIEmbeddedTokenDto | null>(null);
  reportObj!: pbi.Report;
  public readonly BUTTON_SIZE = ButtonSizes.MEDIUM;

  public embedConfig = computed(() =>
    this.#helperService.setConfig(this.powerBIEmbeddedTokenDto(), pbi.models.TokenType.Embed)
  );

  public getAccessibleDashboard = computed(() => {
    const userRoles = this.#authService.userRoles();
    const accessibleDashboards = new Set<string>();

    userRoles.forEach(role => {
      const dashboards = DASHBOARD_ACCESS[role as keyof typeof DASHBOARD_ACCESS] || [];
      dashboards.forEach((dashboard: string) => accessibleDashboards.add(dashboard));
    });

    const dashboard = Array.from(accessibleDashboards).find(
      primaryServiceDashboard =>
        primaryServiceDashboard ===
        getFormattedEnumKeyByValue(CompanyPrimaryService, this.primaryService() as CompanyPrimaryService)
    );

    if (dashboard) {
      this.getDashboard(dashboard);
    }
    return dashboard ?? 'dashboard.title';
  });

  public showDashboardNotConfigured = computed(() => {
    return (this.primaryService() === undefined ? false : true) && this.getAccessibleDashboard() === 'dashboard.title';
  });

  eventHandlersMap = new Map<string, (event?: pbi.service.ICustomEvent<unknown>, embeddedEntity?: pbi.Embed) => void>([
    [
      'loaded',
      (event, report) => {
        this.reportObj = report as pbi.Report;
        console.log('Report has loaded');
      },
    ],
    [
      'rendered',
      () => {
        console.log('Report has rendered');
      },
    ],
    [
      'error',
      event => {
        if (event) {
          console.error(event);
        }
      },
    ],
    ['visualClicked', event => console.log(event)],
    ['pageChanged', event => console.log(event)],
  ]);

  getDashboard(dashboard: string): void {
    if (dashboard) {
      this.#helperService
        .getConfig(dashboard)
        .pipe(takeUntilDestroyed(this.#destroyRef))
        .subscribe({
          next: config => {
            if (config && !config.error) {
              this.powerBIEmbeddedTokenDto.set(config);
            } else {
              this.showErrorMessage.set(true);
              this.powerBIEmbeddedTokenDto.set(null);
              this.setSanitizedMessage(config.error);
            }
          },
        });
    }
  }

  addSupportEmailLink(errorMessage: string): SafeHtml {
    errorMessage = errorMessage.replace(
      this.#translate.instant('dashboard.errorMessage.contactSupportTeam'),
      this.#translate.instant('dashboard.errorMessage.contactSupportMailLink')
    );
    return this.#sanitizer.bypassSecurityTrustHtml(errorMessage);
  }

  setSanitizedMessage(error?: string): void {
    if (error && error.includes(this.#translate.instant('dashboard.errorMessage.contactSupportTeam'))) {
      this.sanitizedMessage.set(this.addSupportEmailLink(error));
    } else {
      this.sanitizedMessage.set(error ?? '');
    }
  }

  toggleFullScreen(): void {
    this.reportObj.fullscreen();
  }
}
